import { createContext, ReactNode, useContext, useState, useEffect } from 'react';
import { buildCdkContract, CDKContract } from '../web3/contract';
import { ethers } from 'ethers';

export type UserType = 'USER' | 'ADMIN' | 'SUPER_ADMIN';

export type ConnectedUser = {
    type: UserType;
    walletAddress: string;
    cdkBalance: number;
    usdcBalance: number;
    countCdkStaked: number;
};

export type ConnectedUserContextType = {
    connectedUser: ConnectedUser | undefined;
    contract: CDKContract | undefined;
    setConnectedUser: (wallet: string) => void;
    disconnectUser: () => void;
    cdkBalance: number;
    usdcBalance: number;
    cdkStaked: number;
    withdrawableCDKs: number;
    reloadAllBalances: () => void;
};

export const ConnectedUserContext = createContext<ConnectedUserContextType | undefined>(undefined);

export const ConnectedUserProvider = ({ children }: { children: ReactNode }) => {
    const [connectedUser, setConnectedUser] = useState<ConnectedUser | undefined>(undefined);
    const [contract, setContract] = useState<CDKContract | undefined>(undefined);
    const [isDisconnecting, setIsDisconnecting] = useState(false); // Add this flag

    const [cdkBalance, setCdkBalance] = useState<number>(0);
    const [usdcBalance, setUsdcBalance] = useState<number>(0);
    const [cdkStaked, setCdkStaked] = useState<number>(0);
    const [withdrawableCDKs, setWithdrawableCDK] = useState<number>(0);

    const disconnectUser = async () => {
        console.log('Disconnecting user...');
        setIsDisconnecting(true); // Set flag to prevent auto-reconnection
        
        // Clear React state
        setConnectedUser(undefined);
        setContract(undefined);
        setCdkBalance(0);
        setUsdcBalance(0);
        setCdkStaked(0);
        setWithdrawableCDK(0);
        
        // Clear localStorage
        localStorage.removeItem('walletconnect');
        localStorage.removeItem('walletConnected');
        localStorage.removeItem('connectedWallet');
        localStorage.removeItem('injected');
        
        // Clear sessionStorage
        sessionStorage.removeItem('walletconnect');
        sessionStorage.removeItem('connectedWallet');
        
        try {
            // Try to revoke permissions for MetaMask
            if (window.ethereum?.request) {
                await window.ethereum.request({
                    method: "wallet_revokePermissions",
                    params: [{ eth_accounts: {} }]
                });
            }
        } catch (error) {
            console.log('Permission revoke not supported:', error);
        }
        
        console.log('User disconnected successfully');
        
        // Reset the flag after a delay to allow for reconnection later
        setTimeout(() => {
            setIsDisconnecting(false);
        }, 1000);
    };

    // Your existing functions...
    const loadCdkUserBalance = async (cdkContract: CDKContract, walletAddress: string) => {
        const rawCdkBalance = await cdkContract.cdkFacet.balanceOf(walletAddress);
        return Number(ethers.formatEther(rawCdkBalance));
    }

    const loadAllCDKStakedForUser = async (cdkContract: CDKContract, walletAddress: string) => {
        const activeStakingRounds = await cdkContract.retrieveAllPublicActiveUnStakingRounds();
        const activeStakingRoundsWithUserStakes = await cdkContract.retrieveUserStakes(walletAddress, activeStakingRounds);
        
        const totalCdkStaked = activeStakingRoundsWithUserStakes.reduce(
            (acc, roundWithStake) => acc + roundWithStake.countCDKStaked, 0
        );
        
        return totalCdkStaked;
    }

    const retrieveWithdrawableCDKs = async (walletAddress: string) => {
        const withdrawableAmount = await buildCdkContract().retrieveAllWithdrawableAmount(walletAddress);
        return Number(withdrawableAmount);
    }

    const setConnectedUserHandler = async (walletAddress: string) => {
        // Don't connect if we're in the middle of disconnecting
        if (isDisconnecting) {
            console.log('Ignoring connection attempt during disconnect');
            return;
        }
        
        const cdkContract = buildCdkContract();
        const userType = 'USER';
        const cdkBalance = await loadCdkUserBalance(cdkContract, walletAddress);
        const usdcBalance = await cdkContract.usdc.balanceOf(walletAddress);
        const allCdkStaked = await loadAllCDKStakedForUser(cdkContract, walletAddress);

        setCdkBalance(Number(cdkBalance));
        setUsdcBalance(Number(usdcBalance));
        setCdkStaked(Number(allCdkStaked));
        setWithdrawableCDK(Number(await retrieveWithdrawableCDKs(walletAddress)));

        setConnectedUser({
            walletAddress,
            type: userType,
            cdkBalance: Number(cdkBalance),
            usdcBalance: Number(usdcBalance),
            countCdkStaked: Number(allCdkStaked),
        });
        setContract(cdkContract);
    };

    // Add this useEffect to handle account changes properly
    useEffect(() => {
        if (window.ethereum) {
            const handleAccountsChanged = (accounts: string[]) => {
                console.log('Accounts changed:', accounts);
                
                // Only disconnect if accounts array is empty AND we're not already disconnecting
                if (accounts.length === 0 && !isDisconnecting) {
                    console.log('No accounts found, disconnecting user');
                    disconnectUser();
                }
                // Don't auto-reconnect to prevent the disconnect/reconnect loop
            };
            
            window.ethereum.on('accountsChanged', handleAccountsChanged);
            return () => {
                window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
            };
        }
    }, [isDisconnecting]);

    // Your other existing functions remain the same...
    const reloadCdkUserBalance = async () => {
        if (connectedUser) {
            const cdkBalance = await loadCdkUserBalance(buildCdkContract(), connectedUser.walletAddress);
            const cdkBalanceNumber = Number(cdkBalance);

            setConnectedUser({ ...connectedUser, cdkBalance: cdkBalanceNumber });
            setCdkBalance(cdkBalanceNumber);
        }
    };

    const reloadUsdcUserBalance = async () => {
        if (connectedUser) {
            const usdcBalance = await contract!.usdc.balanceOf(connectedUser.walletAddress);
            const usdcBalanceNumber = Number(usdcBalance);

            setConnectedUser({ ...connectedUser, usdcBalance: usdcBalanceNumber });
            setUsdcBalance(usdcBalanceNumber);
        }
    };

    const reloadCDKStaked = async () => {
        if (connectedUser && contract) {
            const activeStakingRounds = await contract.retrieveAllPublicActiveUnStakingRounds();
            const activeStakingRoundsWithUserStakes = await contract.retrieveUserStakes(connectedUser.walletAddress, activeStakingRounds);
            
            const totalCdkStaked = activeStakingRoundsWithUserStakes.reduce(
                (acc, roundWithStake) => acc + roundWithStake.countCDKStaked, 0
            );

            setConnectedUser({ ...connectedUser, countCdkStaked: totalCdkStaked });
            setCdkStaked(totalCdkStaked);
        }
    }

    const reloadWithdrawableCDKs = async () => {
        if (connectedUser) {
            const withdrawableCDKs = await retrieveWithdrawableCDKs(connectedUser.walletAddress);
            setWithdrawableCDK(withdrawableCDKs);
        }
    };

    const reloadAllBalances = async () => {
        await reloadCdkUserBalance();
        await reloadUsdcUserBalance();
        await reloadCDKStaked();
        await reloadWithdrawableCDKs();
    }

    return (
        <ConnectedUserContext.Provider
            value={{
                connectedUser,
                setConnectedUser: setConnectedUserHandler,
                disconnectUser,
                contract,
                cdkBalance,
                usdcBalance,
                withdrawableCDKs,
                cdkStaked,
                reloadAllBalances
        }}
        >
            {children}
        </ConnectedUserContext.Provider>
    );
};

export const useConnectedUser = () => {
    const context = useContext(ConnectedUserContext);
    if (!context) {
        throw new Error('useConnectedUser must be used within a ConnectedUserProvider');
    }
    return context;
};